import { useState } from 'react';

import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import { styled, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { isEmpty, map } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import SynonymSetActions from './SynonymSetActions';
import { useUser } from '../../../contexts/UserContext';
import { formatDateTimeUTC } from '../../../utils/timeUtils';
import { formatUserInfo, getInitials } from '../../../utils/utils';
import Text from '../../elements/text/Text';
import NoContent from '../../sections/no-content/NoContent';

const StyledAvatarGroup = styled(AvatarGroup)(({ theme }) => ({
  '& .MuiAvatar-root': {
    fontSize: '0.9rem',
    width: theme.spacing(4) - 2,
    height: theme.spacing(4) - 2,
  },
}));

const StyledIcon = styled(InfoOutlinedIcon)({
  fontSize: '0.9rem',
  marginLeft: 4,
  marginRight: 1,
});

const StyledChatIcon = styled(ChatOutlinedIcon)(({ theme, disabled }) => ({
  color: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
  cursor: disabled ? 'default' : 'pointer',
}));

const SynonymSetTable = ({
  isSearching,
  data,
  handlePageChange,
  ...synonymSetFuncs
}) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { totalPages, content: sets, number: page } = data;
  const [isPageChanging, setIsPageChanging] = useState(false);

  const TABLE_HEADERS = [
    t('synonym.set', { ns: 'project' }),
    t('synonym.editors', { ns: 'project' }),
    t('synonym.lastUpdate', { ns: 'project' }),
  ];

  const handlePagination = async (_, page) => {
    setIsPageChanging(true);
    await handlePageChange(page - 1);
    setIsPageChanging(false);
  };

  const getDisplayWords = (words) => {
    const displayNum = 3;
    if (words.length <= displayNum) return words.join(', ');
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {words.slice(0, displayNum).join(', ')}
        {words.length > displayNum &&
          (isSmallScreen ? (
            '...'
          ) : (
            <Tooltip
              title={
                <>
                  {words.slice(displayNum, words.length).map((word) => (
                    <Box key={word}>{word}</Box>
                  ))}
                </>
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StyledIcon />
                <Text caption>
                  {t('synonym.moreWord', {
                    count: words.length - displayNum,
                    ns: 'project',
                  })}
                </Text>
              </Box>
            </Tooltip>
          ))}
      </Box>
    );
  };

  return (
    <>
      {!isEmpty(sets) ? (
        <>
          <TableContainer>
            <Table size="small" aria-label="set-table">
              <TableHead>
                <TableRow>
                  <TableCell />
                  {TABLE_HEADERS.map((header, i) => (
                    <TableCell align={i !== 0 ? 'center' : 'left'} key={header}>
                      {header}
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sets.map((set) => (
                  <TableRow key={set.id} aria-label="table row">
                    <TableCell width={20}>
                      {set.comment ? (
                        <Tooltip title={set.comment} aria-label="comment">
                          <StyledChatIcon disabled={false} />
                        </Tooltip>
                      ) : (
                        <StyledChatIcon
                          aria-label="no comment"
                          disabled={true}
                        />
                      )}
                    </TableCell>
                    <TableCell style={{ width: '50%' }}>
                      {getDisplayWords(map(set.items, 'value'))}
                    </TableCell>
                    <TableCell
                      align="center"
                      style={{
                        minWidth: '10%',
                      }}
                    >
                      <StyledAvatarGroup
                        aria-label="avatar group"
                        style={{
                          minWidth: '10%',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {set.editorUsers.map((editor, i) => {
                          const formattedEditor = formatUserInfo(editor);
                          const name = formattedEditor.publicName;

                          return (
                            <Tooltip key={i} title={name} test="test">
                              <Avatar
                                key={i}
                                alt={name}
                                src={formattedEditor.icon}
                                style={{
                                  zIndex: set.editorUsers.length - i,
                                }}
                              >
                                {getInitials(name)}
                              </Avatar>
                            </Tooltip>
                          );
                        })}
                      </StyledAvatarGroup>
                    </TableCell>
                    <TableCell align="center">
                      {formatDateTimeUTC(set.updateDate, user?.timeZone)}
                    </TableCell>
                    <TableCell>
                      <SynonymSetActions set={set} {...synonymSetFuncs} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Pagination
              count={totalPages}
              page={page + 1 || 1}
              shape="rounded"
              disabled={isPageChanging}
              onChange={handlePagination}
            />
          </Box>
        </>
      ) : (
        <NoContent
          title={
            isSearching
              ? t('noSynonymSetResult', { ns: 'descriptions' })
              : t('noSynonymSet', { ns: 'descriptions' })
          }
        />
      )}
    </>
  );
};

SynonymSetTable.propTypes = {
  isSearching: PropTypes.bool,
  data: PropTypes.shape({
    content: PropTypes.array,
    totalPages: PropTypes.number,
    number: PropTypes.number,
  }),
  handlePageChange: PropTypes.func.isRequired,
};

export default SynonymSetTable;
